import type { College } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectIsSharedSessionActive } from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession';
import {
  useAddUserCollegeMutation,
  useGetUserCollegesQuery,
  useRemoveUserCollegeMutation,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.api';

const STRINGS = appStrings.modules.myColleges;

type ToggleState = Readonly<{ isInMyColleges: boolean; addFailed: boolean; removeFailed: boolean }>;

type SavableCollege = Pick<College, 'academic_profile_id' | 'ipeds_id'>;

export const useMyCollegeToggle = (college: SavableCollege | null | undefined) => {
  const isSharedSession = useAppSelector(selectIsSharedSessionActive);
  const isAvailable = Boolean(college?.ipeds_id) && !isSharedSession;

  const { data } = useGetUserCollegesQuery(undefined, { skip: !isAvailable });
  const [addUserCollege, { isLoading: isAdding, isError: addFailed }] = useAddUserCollegeMutation();
  const [removeUserCollege, { isLoading: isRemoving, isError: removeFailed }] =
    useRemoveUserCollegeMutation();

  const saved = data?.colleges.find(
    (candidate) => candidate.academic_profile_id === college?.academic_profile_id,
  );

  const toggle = () => {
    if (!college || !isAvailable || isAdding || isRemoving) return;
    if (saved) {
      removeUserCollege(saved.id);
    } else {
      addUserCollege({ ipeds_id: college.ipeds_id });
    }
  };

  return {
    isAvailable: isAvailable && Boolean(data),
    isInMyColleges: Boolean(saved),
    tier: saved?.tier ?? null,
    addFailed,
    removeFailed,
    toggle,
  };
};

export const myCollegeTooltip = (state: ToggleState) =>
  (state.addFailed && STRINGS.addFailed) ||
  (state.removeFailed && STRINGS.removeFailed) ||
  (state.isInMyColleges && STRINGS.remove) ||
  '';

export const useSavedInstitutionIds = () => {
  const isSharedSession = useAppSelector(selectIsSharedSessionActive);
  const { data } = useGetUserCollegesQuery(undefined, { skip: isSharedSession });

  return data && new Set(data.colleges.map((college) => college.institution_id));
};
